import { Text } from "@/components/podkit/typography/Text";
import IconBubbleText6 from "central-icons/IconBubbleText6";
import IconConsoleSimple from "central-icons/IconConsoleSimple";
import type React from "react";
import { useState, useMemo, useEffect, type FC, type ReactNode, useCallback } from "react";
import { Button } from "@/components/flexkit/Button";
import { AutomationStepKindPill } from "@/components/automations/AutomationStepKindPill";
import { AutomationStepNode, type AutomationStepNodeVariant } from "@/components/automations/AutomationStepNode";
import { AutomationStepArrow } from "@/components/automations/AutomationStepArrow";
import type {
    WorkflowEditFormPullRequestStepData,
    WorkflowEditFormReportStepData,
    WorkflowEditFormStepData,
    WorkflowEditFormTriggerData,
    WorkflowEditFormLimitsData,
} from "@/components/automations/automation-edit-form-data";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { useRef, createContext, useContext } from "react";
import {
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import {
    DndContext,
    DragOverlay,
    useDraggable,
    useDroppable,
    PointerSensor,
    TouchSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import type { Modifier } from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { cn } from "@/components/podkit/lib/cn";
import { haptic } from "@/utils/haptic";
import IconPullRequest from "central-icons/IconPullRequest";
import IconTable from "central-icons/IconTable";
import { AutomationTriggerNode } from "@/components/automations/AutomationTriggerNode";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { pluralizeWithCount } from "@/utils/strings";
import { Pill } from "@/components/podkit/status/Pill";

const ActiveDragContext = createContext<string | null>(null);

type PendingLiftContextValue = {
    id: string | null;
    set: (id: string | null) => void;
};
const noop = () => {
    /* default context value — overridden by provider */
};
const PendingLiftContext = createContext<PendingLiftContextValue>({ id: null, set: noop });

/** Shared between TouchSensor activation constraint and the visual lift timer */
const LONG_PRESS_DELAY_MS = 250;

const DRAG_OFFSET_X = 16;
const restrictToVerticalAxisWithOffset: Modifier = ({ transform }) => ({
    ...transform,
    x: DRAG_OFFSET_X,
});

const TRUNCATE_LINES = 3;
const MAX_VISIBLE_COLUMNS = 10;

type ExpandableContentProps = {
    content: string;
    isMonospace?: boolean;
    /** When true, disables touch scrolling on the content area so drag gestures pass through */
    isDraggable?: boolean;
};

const ExpandableContent: FC<ExpandableContentProps> = ({ content, isMonospace = false, isDraggable = false }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const { displayText, shouldTruncate, hiddenLines } = useMemo(() => {
        const lines = content.split("\n");
        const truncated = lines.length > TRUNCATE_LINES;
        const displayLines = truncated && !isExpanded ? lines.slice(0, TRUNCATE_LINES) : lines;

        return {
            displayText: displayLines.join("\n"),
            shouldTruncate: truncated,
            hiddenLines: lines.length - TRUNCATE_LINES,
        };
    }, [content, isExpanded]);

    const onClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
            setIsExpanded((expanded) => !expanded);
        },
        [setIsExpanded],
    );

    return (
        <div
            className={cn("flex w-full flex-col rounded-lg border border-border-subtle", { "touch-none": isDraggable })}
        >
            <ScrollArea
                orientation="both"
                className={cn("flex max-h-[300px] flex-col", {
                    "max-h-none": !isExpanded && !shouldTruncate,
                    "pointer-events-none": isDraggable,
                })}
            >
                <Text
                    className={cn("whitespace-pre-wrap break-words px-3 py-2 text-base font-normal", {
                        "font-mono": isMonospace,
                    })}
                >
                    {displayText}
                </Text>
            </ScrollArea>
            {shouldTruncate && (
                <div className="flex shrink-0 items-center justify-center border-t border-border-subtle p-1">
                    <Button
                        variant="ghost"
                        size="xs"
                        onClick={onClick}
                        className="text-xs text-content-secondary"
                        data-tracking-id="expand-step-content"
                    >
                        {isExpanded ? "Show less" : `Show ${hiddenLines} more lines`}
                    </Button>
                </div>
            )}
        </div>
    );
};

export type AutomationStepListProps = {
    trigger?: WorkflowEditFormTriggerData;
    executor?: PlainMessage<Subject>;
    limits?: WorkflowEditFormLimitsData;
    steps: WorkflowEditFormStepData[];
    editable?: boolean;
    isTriggerSelected?: boolean;
    selectedStepIndex?: number | null;
    onTriggerSelect?: () => void;
    onStepSelect?: (index: number) => void;
    onStepRemove?: (index: number) => void;
    onStepsReorder?: (newSteps: WorkflowEditFormStepData[]) => void;
    footer?: ReactNode;
};

export const AutomationStepList: FC<AutomationStepListProps> = ({
    trigger,
    executor,
    limits,
    steps,
    editable = false,
    isTriggerSelected = false,
    selectedStepIndex = null,
    onTriggerSelect,
    onStepSelect,
    onStepRemove,
    onStepsReorder,
    footer,
}) => {
    const [activeId, setActiveId] = useState<string | null>(null);
    const [pendingLiftId, setPendingLiftId] = useState<string | null>(null);
    const pendingLiftValue = useMemo<PendingLiftContextValue>(
        () => ({ id: pendingLiftId, set: setPendingLiftId }),
        [pendingLiftId],
    );

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: LONG_PRESS_DELAY_MS,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const resetDragState = useCallback(() => {
        setActiveId(null);
        setPendingLiftId(null);
    }, []);

    const onDragStart = useCallback((event: DragStartEvent) => {
        setPendingLiftId(null);
        setActiveId(event.active.id as string);
        haptic("light");
    }, []);

    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        const fromIndex = active.data.current?.stepIndex as number;
        const toIndex = over?.data.current?.stepIndex as number;

        if (fromIndex !== undefined && toIndex !== undefined && fromIndex !== toIndex && onStepsReorder) {
            const newSteps = [...steps];
            const [stepToMove] = newSteps.splice(fromIndex, 1);
            const adjustedToIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
            newSteps.splice(adjustedToIndex, 0, stepToMove);
            onStepsReorder(newSteps);
        }
        resetDragState();
    };

    const activeStep = activeId ? steps.find((step) => step.id === activeId) : null;
    const activeStepIndex = activeId ? steps.findIndex((step) => step.id === activeId) : -1;

    return (
        <DndContext
            sensors={sensors}
            modifiers={[restrictToVerticalAxisWithOffset]}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={resetDragState}
        >
            <ActiveDragContext.Provider value={activeId}>
                <PendingLiftContext.Provider value={pendingLiftValue}>
                    <ol className="flex min-h-full w-full max-w-[720px] flex-1 flex-col items-center">
                        <AutomationTriggerNode
                            limits={limits}
                            trigger={trigger}
                            executor={executor}
                            selected={isTriggerSelected}
                            onClick={onTriggerSelect}
                            data-tracking-id="ontriggerselect"
                        />
                        {steps.length > 0 && (
                            <li className="w-full">
                                <AutomationStepDropZone
                                    editable={editable}
                                    stepIndex={-1}
                                    stepId={`dropzone-top-${steps[0].id}`}
                                    targetIndex={0}
                                    totalSteps={steps.length}
                                />
                            </li>
                        )}
                        {steps.map((step, index) => (
                            <AutomationStepItem
                                key={step.id}
                                step={step}
                                stepIndex={index}
                                isLast={index === steps.length - 1}
                                editable={editable}
                                isSelected={selectedStepIndex === index}
                                onSelect={onStepSelect}
                                onRemove={onStepRemove}
                            />
                        ))}
                        {editable && steps.length > 0 && (
                            <li className="relative w-full" aria-hidden>
                                <AutomationStepDropZone
                                    editable={editable}
                                    stepIndex={steps.length - 1}
                                    stepId={`dropzone-bottom-${steps[steps.length - 1].id}`}
                                    targetIndex={steps.length}
                                    totalSteps={steps.length}
                                    hidden
                                />
                            </li>
                        )}
                        {footer}
                    </ol>
                    <DragOverlay
                        dropAnimation={{
                            duration: 300,
                            easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
                        }}
                    >
                        {activeStep && activeStepIndex !== -1 ? (
                            <DragOverlayContent step={activeStep} stepIndex={activeStepIndex} />
                        ) : null}
                    </DragOverlay>
                </PendingLiftContext.Provider>
            </ActiveDragContext.Provider>
        </DndContext>
    );
};

type AutomationStepItemProps = {
    step: WorkflowEditFormStepData;
    stepIndex: number;
    isLast: boolean;
    editable: boolean;
    isSelected: boolean;
    onSelect?: (index: number) => void;
    onRemove?: (index: number) => void;
};

const AutomationStepItem: FC<AutomationStepItemProps> = ({
    step,
    stepIndex,
    isLast,
    editable,
    isSelected,
    onSelect,
    onRemove,
}) => {
    let node: React.ReactNode | null = null;
    switch (step.type) {
        case "command":
            node = (
                <AutomationTaskStepNode
                    content={step.content}
                    editable={editable}
                    stepIndex={stepIndex}
                    stepId={step.id}
                    isSelected={isSelected}
                    onSelect={onSelect}
                    onRemove={onRemove}
                />
            );
            break;
        case "prompt":
            node = (
                <AutomationAgentStepNode
                    content={step.content}
                    editable={editable}
                    stepIndex={stepIndex}
                    stepId={step.id}
                    isSelected={isSelected}
                    onSelect={onSelect}
                    onRemove={onRemove}
                />
            );
            break;
        case "pr":
            node = (
                <AutomationStepPullRequestNode
                    data={step}
                    editable={editable}
                    stepIndex={stepIndex}
                    stepId={step.id}
                    isSelected={isSelected}
                    onSelect={onSelect}
                    onRemove={onRemove}
                />
            );
            break;
        case "report":
            node = (
                <AutomationStepReportNode
                    data={step}
                    editable={editable}
                    stepIndex={stepIndex}
                    stepId={step.id}
                    isSelected={isSelected}
                    onSelect={onSelect}
                    onRemove={onRemove}
                />
            );
            break;
        default:
            node = null;
            break;
    }

    if (!node) {
        return null;
    }

    if (isLast) {
        return (
            <li className="w-full">
                <DraggableAutomationStepItem editable={editable} stepIndex={stepIndex} stepId={step.id}>
                    {node}
                </DraggableAutomationStepItem>
            </li>
        );
    }

    return (
        <li className="w-full">
            <DraggableAutomationStepItem editable={editable} stepIndex={stepIndex} stepId={step.id}>
                {node}
            </DraggableAutomationStepItem>
            <AutomationStepDropZone editable={editable} stepIndex={stepIndex} stepId={step.id} />
        </li>
    );
};

const DraggableAutomationStepItem: FC<{
    children: React.ReactNode;
    stepIndex: number;
    stepId: string;
    editable: boolean;
}> = ({ children, stepIndex, stepId, editable }) => {
    const activeId = useContext(ActiveDragContext);
    const { id: pendingLiftId, set: setPendingLiftId } = useContext(PendingLiftContext);
    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Clear pending timer on unmount to avoid stale state updates
    useEffect(
        () => () => {
            if (longPressTimer.current !== null) clearTimeout(longPressTimer.current);
        },
        [],
    );

    const { setNodeRef, listeners, attributes } = useDraggable({
        id: stepId,
        disabled: !editable,
        data: { stepIndex },
    });

    const isBeingDragged = activeId === stepId;
    const isPendingLift = pendingLiftId === stepId && !isBeingDragged;

    // Track drag state in a ref so touch handlers always see the latest value
    const isDraggingRef = useRef(false);
    isDraggingRef.current = activeId !== null;

    const clearTimer = useCallback(() => {
        if (longPressTimer.current !== null) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    }, []);

    const handleTouchStart = useCallback(() => {
        if (!editable) return;
        clearTimer();
        longPressTimer.current = setTimeout(() => {
            setPendingLiftId(stepId);
            navigator.vibrate?.(10);
        }, LONG_PRESS_DELAY_MS);
    }, [editable, stepId, setPendingLiftId, clearTimer]);

    const handleTouchEnd = useCallback(() => {
        clearTimer();
        // Don't clear pending lift during an active drag — the parent's
        // onDragStart/onDragEnd/onDragCancel manage that state transition.
        if (!isDraggingRef.current) {
            setPendingLiftId(null);
        }
    }, [clearTimer, setPendingLiftId]);

    return (
        <div
            ref={setNodeRef}
            className={cn(
                "w-full transition-transform duration-150",
                { "select-none touch-none": editable },
                { "opacity-40": isBeingDragged },
                { "scale-[1.02] rounded-lg shadow-lg": isPendingLift },
            )}
            {...(editable ? listeners : {})}
            {...attributes}
            onTouchStartCapture={handleTouchStart}
            onTouchEndCapture={handleTouchEnd}
            onTouchCancelCapture={handleTouchEnd}
        >
            {children}
        </div>
    );
};

const AutomationStepDropZone: FC<{
    stepIndex: number;
    stepId: string;
    editable: boolean;
    targetIndex?: number;
    totalSteps?: number;
    hidden?: boolean;
}> = ({ stepIndex, stepId, editable, targetIndex, totalSteps, hidden = false }) => {
    const finalTargetIndex = targetIndex !== undefined ? targetIndex : stepIndex + 1;
    const { isOver, setNodeRef, active } = useDroppable({
        id: stepId,
        disabled: !editable,
        data: { stepIndex: finalTargetIndex },
    });

    let isDroppable;
    if (isOver && active?.data.current?.stepIndex !== undefined) {
        const activeIndex = active.data.current.stepIndex as number;
        if (targetIndex === 0) {
            isDroppable = activeIndex !== 0;
        } else if (targetIndex !== undefined && totalSteps !== undefined && targetIndex === totalSteps) {
            isDroppable = activeIndex !== totalSteps - 1;
        } else {
            isDroppable = !(activeIndex - 1 === stepIndex || activeIndex === stepIndex);
        }
    } else {
        isDroppable = false;
    }

    if (hidden) {
        return (
            <div ref={setNodeRef} className="absolute inset-x-0 top-0 flex h-4 items-center pl-6">
                <div className="mr-3 w-6 shrink-0" />
                <hr
                    className={cn("h-[1.5px] flex-1 rounded-full border-0 bg-content-brand", {
                        "opacity-0": !isDroppable,
                    })}
                />
            </div>
        );
    }

    return (
        <div
            ref={setNodeRef}
            className="-mt-px flex w-full flex-row items-start pl-6 transition-all duration-300 ease-in-out"
        >
            <AutomationStepArrow className={cn("mr-3 text-content-secondary", { "text-content-brand": isDroppable })} />
            <hr
                className={cn("h-[1.5px] flex-1 self-center rounded-full border-0 bg-content-brand", {
                    "opacity-0": !isDroppable,
                })}
            />
        </div>
    );
};

type StepNodeProps = {
    editable: boolean;
    stepIndex: number;
    stepId: string;
    isSelected: boolean;
    onSelect?: (index: number) => void;
    onRemove?: (index: number) => void;
};

const AutomationTaskStepNode: FC<StepNodeProps & { content: string }> = ({
    content,
    editable,
    stepIndex,
    stepId,
    isSelected,
    onSelect,
    onRemove,
}) => {
    const nodeRef = useRef<HTMLDivElement>(null);
    const activeId = useContext(ActiveDragContext);
    const isDragging = activeId === stepId;

    let variant: AutomationStepNodeVariant;
    if (isDragging) {
        variant = "dragging";
    } else if (isSelected) {
        variant = "selected";
    } else if (editable) {
        variant = "editable";
    } else {
        variant = "readOnly";
    }

    const canClick = editable && !isSelected;

    const handleClick = () => {
        if (canClick && onSelect) {
            onSelect(stepIndex);
        }
    };

    return (
        <AutomationStepNode
            ref={nodeRef}
            className={cn("w-full", { "cursor-pointer": canClick })}
            variant={variant}
            onClick={canClick ? handleClick : undefined}
            data-tracking-id={canClick ? "edit-task-step" : undefined}
            showDragHandle={editable}
        >
            <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
                <div className="flex w-full items-center justify-between">
                    <AutomationStepKindPill variant="warning" LeadingIcon={IconConsoleSimple}>
                        Shell Script
                    </AutomationStepKindPill>
                    {editable ? (
                        <AutomationStepNodeEditMenu stepIndex={stepIndex} onSelect={onSelect} onRemove={onRemove} />
                    ) : null}
                </div>
                <ExpandableContent content={content} isMonospace={true} isDraggable={editable} />
            </div>
        </AutomationStepNode>
    );
};

const AutomationAgentStepNode: FC<StepNodeProps & { content: string }> = ({
    content,
    editable,
    stepIndex,
    stepId,
    isSelected,
    onSelect,
    onRemove,
}) => {
    const nodeRef = useRef<HTMLDivElement>(null);
    const activeId = useContext(ActiveDragContext);
    const isDragging = activeId === stepId;

    let variant: AutomationStepNodeVariant;
    if (isDragging) {
        variant = "dragging";
    } else if (isSelected) {
        variant = "selected";
    } else if (editable) {
        variant = "editable";
    } else {
        variant = "readOnly";
    }

    const canClick = editable && !isSelected;

    const handleClick = () => {
        if (canClick && onSelect) {
            onSelect(stepIndex);
        }
    };

    return (
        <AutomationStepNode
            ref={nodeRef}
            className={cn("w-full", { "cursor-pointer": canClick })}
            variant={variant}
            onClick={canClick ? handleClick : undefined}
            data-tracking-id={canClick ? "edit-prompt-step" : undefined}
            showDragHandle={editable}
        >
            <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
                <div className="flex w-full items-center justify-between">
                    <AutomationStepKindPill variant="brand" LeadingIcon={IconBubbleText6}>
                        Prompt
                    </AutomationStepKindPill>
                    {editable ? (
                        <AutomationStepNodeEditMenu stepIndex={stepIndex} onSelect={onSelect} onRemove={onRemove} />
                    ) : null}
                </div>
                <ExpandableContent content={content} isMonospace={false} isDraggable={editable} />
            </div>
        </AutomationStepNode>
    );
};

const AutomationStepPullRequestNode: FC<StepNodeProps & { data: WorkflowEditFormPullRequestStepData }> = ({
    data,
    editable,
    stepIndex,
    stepId,
    isSelected,
    onSelect,
    onRemove,
}) => {
    const nodeRef = useRef<HTMLDivElement>(null);
    const activeId = useContext(ActiveDragContext);
    const isDragging = activeId === stepId;

    let variant: AutomationStepNodeVariant;
    if (isDragging) {
        variant = "dragging";
    } else if (isSelected) {
        variant = "selected";
    } else if (editable) {
        variant = "editable";
    } else {
        variant = "readOnly";
    }

    const canClick = editable && !isSelected;

    const handleClick = () => {
        if (canClick && onSelect) {
            onSelect(stepIndex);
        }
    };

    return (
        <AutomationStepNode
            ref={nodeRef}
            className={cn("w-full", { "cursor-pointer": canClick })}
            variant={variant}
            onClick={canClick ? handleClick : undefined}
            data-tracking-id={canClick ? "edit-pr-step" : undefined}
            showDragHandle={editable}
        >
            <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
                <div className="flex w-full items-center justify-between">
                    <AutomationStepKindPill variant="success" LeadingIcon={IconPullRequest}>
                        {data.isDraft ? "Draft pull request" : "Pull request"}
                    </AutomationStepKindPill>
                    {editable ? (
                        <AutomationStepNodeEditMenu stepIndex={stepIndex} onSelect={onSelect} onRemove={onRemove} />
                    ) : null}
                </div>
                <div className="flex w-full flex-col gap-1">
                    <Text className="font-medium">{data.title}</Text>
                    {data.description && (
                        <ExpandableContent content={data.description} isMonospace={false} isDraggable={editable} />
                    )}
                    <Text className="pt-2 text-content-muted">
                        <span className="text-sm text-content-muted">Branch:</span>{" "}
                        <span className="font-mono text-sm">{data.branch}</span>
                    </Text>
                </div>
            </div>
        </AutomationStepNode>
    );
};

const AutomationStepReportNode: FC<StepNodeProps & { data: WorkflowEditFormReportStepData }> = ({
    data,
    editable,
    stepIndex,
    stepId,
    isSelected,
    onSelect,
    onRemove,
}) => {
    const nodeRef = useRef<HTMLDivElement>(null);
    const activeId = useContext(ActiveDragContext);
    const isDragging = activeId === stepId;

    let variant: AutomationStepNodeVariant;
    if (isDragging) {
        variant = "dragging";
    } else if (isSelected) {
        variant = "selected";
    } else if (editable) {
        variant = "editable";
    } else {
        variant = "readOnly";
    }

    const canClick = editable && !isSelected;

    const handleClick = () => {
        if (canClick && onSelect) {
            onSelect(stepIndex);
        }
    };

    return (
        <AutomationStepNode
            ref={nodeRef}
            className={cn("w-full", { "cursor-pointer": canClick })}
            variant={variant}
            onClick={canClick ? handleClick : undefined}
            data-tracking-id={canClick ? "edit-report-step" : undefined}
            showDragHandle={editable}
        >
            <div className="flex min-w-0 flex-1 flex-col items-start gap-2.5">
                <div className="flex w-full items-center justify-between">
                    <AutomationStepKindPill variant="brand-2" LeadingIcon={IconTable}>
                        Report
                    </AutomationStepKindPill>
                    {editable ? (
                        <AutomationStepNodeEditMenu stepIndex={stepIndex} onSelect={onSelect} onRemove={onRemove} />
                    ) : null}
                </div>
                <div className="flex w-full flex-col gap-1">
                    <Text className="text-sm font-medium text-content-primary">
                        {pluralizeWithCount(data.outputs.length, data.outputs.length > MAX_VISIBLE_COLUMNS, "Output")}
                    </Text>
                    {data.outputs.length > 0 && (
                        <div className="flex flex-col rounded-lg border border-border-subtle bg-surface-input">
                            {data.outputs.slice(0, MAX_VISIBLE_COLUMNS).map((output) => (
                                <div
                                    key={output.id}
                                    className="flex items-center gap-3 border-b border-border-subtle px-2 py-3 last:border-b-0"
                                >
                                    <span className="text-base text-content-primary">{output.title || output.key}</span>
                                    <Pill size="md" variant="info" className="capitalize">
                                        {output.schemaType}
                                    </Pill>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </AutomationStepNode>
    );
};

const AutomationStepNodeEditMenu: FC<{
    stepIndex: number;
    onSelect?: (index: number) => void;
    onRemove?: (index: number) => void;
}> = ({ stepIndex, onSelect, onRemove }) => {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="xs"
                    LeadingIcon={IconEllipsisHorizontal}
                    aria-label="Edit node"
                    onPointerDown={(e) => {
                        e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                    }}
                />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-w-40" align="end">
                <DropdownMenuItem
                    onPointerDown={(e) => {
                        e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                    }}
                    onClick={(event) => {
                        event.stopPropagation();
                        onSelect?.(stepIndex);
                    }}
                    data-tracking-id="edit"
                >
                    Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                    variant="destructive"
                    onPointerDown={(e) => {
                        e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                    }}
                    onClick={(event) => {
                        event.stopPropagation();
                        onRemove?.(stepIndex);
                    }}
                    data-tracking-id="remove-step"
                >
                    Remove step
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const DragOverlayContent: FC<{ step: WorkflowEditFormStepData; stepIndex: number }> = ({ step, stepIndex }) => {
    let child: React.ReactNode | null = null;
    switch (step.type) {
        case "command":
            child = (
                <AutomationTaskStepNode
                    content={step.content}
                    editable
                    stepIndex={stepIndex}
                    stepId={step.id}
                    isSelected={false}
                />
            );
            break;
        case "prompt":
            child = (
                <AutomationAgentStepNode
                    content={step.content}
                    editable
                    stepIndex={stepIndex}
                    stepId={step.id}
                    isSelected={false}
                />
            );
            break;
        case "pr":
            child = (
                <AutomationStepPullRequestNode
                    data={step}
                    editable
                    stepIndex={stepIndex}
                    stepId={step.id}
                    isSelected={false}
                />
            );
            break;
        case "report":
            child = (
                <AutomationStepReportNode
                    data={step}
                    editable
                    stepIndex={stepIndex}
                    stepId={step.id}
                    isSelected={false}
                />
            );
            break;
        default:
            child = null;
            break;
    }
    if (!child) {
        return null;
    }
    return <div className="scale-[1.02] rounded-lg shadow-lg">{child}</div>;
};
